import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";

type Product = "BusinessOne" | "S4Hana";
type TestStatus = "idle" | "testing" | "success" | "error";
type Step = 1 | 2 | 3;

interface SapForm {
  product: Product;
  sapUrl: string;
  companyDb: string;
  b1Username: string;
  b1Password: string;
  tokenUrl: string;
  clientId: string;
  clientSecret: string;
  allowSelfSignedCert: boolean;
  enabled: boolean;
}

const EMPTY_FORM: SapForm = {
  product: "BusinessOne",
  sapUrl: "",
  companyDb: "",
  b1Username: "",
  b1Password: "",
  tokenUrl: "",
  clientId: "",
  clientSecret: "",
  allowSelfSignedCert: false,
  enabled: true,
};

const SETUP_CHECKLIST = [
  "Service Layer is running on a dedicated server with at least 1 GB RAM",
  "Port 50000 (HTTPS) is accessible from the internet or via VPN",
  "A dedicated service account exists (don't use your main SAP login)",
  "The service account has SL_ADMIN + C_SRLS authorization in SAP",
  "A daily Service Layer restart job is scheduled at 2 AM (prevents memory exhaustion)",
  "SSL certificate is trusted (self-signed certs require the \"Allow self-signed cert\" option below)",
];

function normalizeUrl(raw: string): string {
  const val = raw.trim();
  if (!val) return val;
  // If it looks like host:port or host:port/path without a scheme, prepend https://
  if (!/^https?:\/\//i.test(val)) {
    return "https://" + val;
  }
  return val;
}

export default function SapCreate() {
  const [form, setForm] = useState<SapForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [existingId, setExistingId] = useState<number | null>(null);
  const [step, setStep] = useState<Step>(1);
  const [setupOpen, setSetupOpen] = useState(false);
  const [testStatus, setTestStatus] = useState<TestStatus>("idle");
  const [testMessage, setTestMessage] = useState<string>("");
  const [skipTest, setSkipTest] = useState(false);
  const navigate = useNavigate();
  const api = useApi();

  const load = useCallback(async () => {
    try {
      const resp = await api.messaging.get("Sap/config");
      if (resp.ok) {
        const data = await resp.json();
        const config = Array.isArray(data) ? data[0] : data;
        if (config) {
          setExistingId(config.id);
          setForm({
            product: config.product ?? "BusinessOne",
            sapUrl: config.sapUrl ?? "",
            companyDb: config.companyDb ?? "",
            b1Username: config.b1Username ?? "",
            b1Password: "",
            tokenUrl: config.tokenUrl ?? "",
            clientId: config.clientId ?? "",
            clientSecret: "",
            allowSelfSignedCert: config.allowSelfSignedCert ?? false,
            enabled: config.enabled ?? true,
          });
          // If editing existing config, skip straight to step 2
          setStep(2);
        }
      }
    } catch (ex: any) {
      console.error("SapCreate load error", ex.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const set = (field: keyof SapForm, value: string | boolean) => {
    setForm((f) => ({ ...f, [field]: value }));
    // Reset test status on any form change
    setTestStatus("idle");
    setTestMessage("");
    setSkipTest(false);
  };

  const handleUrlBlur = () => {
    const normalized = normalizeUrl(form.sapUrl);
    if (normalized !== form.sapUrl) {
      setForm((f) => ({ ...f, sapUrl: normalized }));
    }
  };

  const buildPayload = () => {
    const payload: any = {
      product: form.product,
      sapUrl: form.sapUrl.trim(),
      allowSelfSignedCert: form.allowSelfSignedCert,
      enabled: form.enabled,
    };
    if (form.product === "BusinessOne") {
      payload.companyDb = form.companyDb.trim();
      payload.b1Username = form.b1Username.trim();
      if (form.b1Password) payload.b1Password = form.b1Password;
    } else {
      payload.tokenUrl = form.tokenUrl.trim();
      payload.clientId = form.clientId.trim();
      if (form.clientSecret) payload.clientSecret = form.clientSecret;
    }
    if (existingId) payload.id = existingId;
    return payload;
  };

  const handleTest = async () => {
    setTestStatus("testing");
    setTestMessage("");
    try {
      const resp = await api.messaging.post("Sap/config/test", buildPayload());
      if (resp.ok) {
        setTestStatus("success");
        setTestMessage("Connected to SAP Business One. Service Layer responding.");
      } else {
        const body = await resp.text().catch(() => "");
        setTestStatus("error");
        setTestMessage(body || `Connection failed (${resp.status})`);
      }
    } catch (ex: any) {
      setTestStatus("error");
      setTestMessage(ex?.message ?? "Unexpected error during test");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      const resp = await api.messaging.post("Sap/config", buildPayload());
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        setError(body || `Save failed (${resp.status})`);
        return;
      }
      navigate("/integrations/sap");
    } catch (ex: any) {
      setError(ex?.message ?? "Unexpected error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const isB1 = form.product === "BusinessOne";
  const canSave = testStatus === "success" || skipTest;

  // Step indicators
  const steps = [
    { num: 1, label: "Product" },
    { num: 2, label: "Connection" },
    { num: 3, label: "Test" },
  ];

  return (
    <div className="max-w-2xl">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          {existingId ? "Edit SAP Connection" : "Connect SAP"}
        </h1>
        <p className="text-gray-500 mt-1 text-sm">
          Connect your SAP instance to enable AI-powered customer lookups, orders, inventory, and service calls from chat.
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-7">
        {steps.map((s, i) => (
          <React.Fragment key={s.num}>
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 transition-colors ${
                  step === s.num
                    ? "border-blue-600 bg-blue-600 text-white"
                    : step > s.num
                    ? "border-green-500 bg-green-500 text-white"
                    : "border-gray-300 bg-white text-gray-400"
                }`}
              >
                {step > s.num ? (
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : s.num}
              </div>
              <span className={`text-sm font-medium ${step === s.num ? "text-gray-900" : "text-gray-400"}`}>
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-px mx-3 ${step > s.num ? "bg-green-400" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        ))}
        <div className={`flex items-center gap-2 ml-3 ${step === 3 && testStatus === "success" ? "opacity-100" : "opacity-40"}`}>
          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${testStatus === "success" ? "border-green-500 bg-green-500 text-white" : "border-gray-300 bg-white text-gray-400"}`}>
            {testStatus === "success" ? (
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : "✓"}
          </div>
          <span className="text-sm font-medium text-gray-400">Done</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* ── STEP 1: Product selection ── */}
        {step === 1 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-slate-800 mb-1">Choose your SAP product</h2>
              <p className="text-sm text-slate-500">Select which SAP platform you are connecting to.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {(["BusinessOne", "S4Hana"] as Product[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => set("product", p)}
                  className={`flex flex-col gap-1 p-4 rounded-xl border-2 text-left transition-colors ${
                    form.product === p
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
                >
                  <span className={`text-sm font-semibold ${form.product === p ? "text-blue-700" : "text-gray-800"}`}>
                    {p === "BusinessOne" ? "SAP Business One" : "SAP S/4HANA"}
                  </span>
                  <span className="text-xs text-gray-500">
                    {p === "BusinessOne"
                      ? "Service Layer API (username + password)"
                      : "OAuth 2.0 / BTP (early access)"}
                  </span>
                </button>
              ))}
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={() => setStep(2)}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Credentials ── */}
        {step === 2 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800">
                  {isB1 ? "SAP Business One — Service Layer" : "SAP S/4HANA — OAuth 2.0 / BTP"}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  {isB1 ? "Enter your Service Layer endpoint and service account credentials." : "Enter your BTP XSUAA token endpoint and OAuth client credentials."}
                </p>
              </div>
              <button type="button" onClick={() => setStep(1)} className="text-xs text-blue-600 hover:underline">
                ← Change product
              </button>
            </div>

            {/* S/4HANA early access warning */}
            {!isB1 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Early access:</strong> SAP S/4HANA support is in early access. Your service account needs API access enabled in SAP BTP with the following scopes:{" "}
                <code className="text-xs bg-amber-100 rounded px-1">API_BUSINESS_PARTNER</code>,{" "}
                <code className="text-xs bg-amber-100 rounded px-1">API_SALES_ORDER_SRV</code>,{" "}
                <code className="text-xs bg-amber-100 rounded px-1">API_BILLING_DOCUMENT_SRV</code>,{" "}
                <code className="text-xs bg-amber-100 rounded px-1">API_PRODUCT_SRV</code>,{" "}
                <code className="text-xs bg-amber-100 rounded px-1">API_MATERIAL_STOCK_SRV</code>.
              </div>
            )}

            {/* Setup Requirements collapsible */}
            {isB1 && (
              <div className="rounded-lg border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setSetupOpen((v) => !v)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm text-slate-600 hover:bg-slate-50 transition"
                >
                  <span className="font-medium">Setup Requirements</span>
                  <svg
                    className={`w-4 h-4 text-slate-400 transition-transform ${setupOpen ? "rotate-180" : ""}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {setupOpen && (
                  <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50">
                    <p className="text-xs text-slate-500 mt-3 mb-3">Before connecting, confirm your SAP environment is ready:</p>
                    <ul className="space-y-2">
                      {SETUP_CHECKLIST.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                          <span className="mt-0.5 w-4 h-4 rounded border border-slate-300 bg-white shrink-0 flex items-center justify-center text-slate-300">☐</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* URL + CompanyDB — two column on desktop */}
            <div className={`grid gap-4 ${isB1 ? "sm:grid-cols-2" : "grid-cols-1"}`}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isB1 ? "Service Layer URL" : "SAP Host URL"}
                </label>
                <input
                  type="text"
                  required
                  value={form.sapUrl}
                  onChange={(e) => set("sapUrl", e.target.value)}
                  onBlur={handleUrlBlur}
                  placeholder={isB1 ? "192.168.1.100:50000" : "https://your-s4hana.example.com"}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  {isB1
                    ? "Tip: Use HTTPS. Default port is 50000. Will auto-format on blur."
                    : "Base URL of your S/4HANA OData API gateway."}
                </p>
              </div>

              {isB1 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Database</label>
                  <input
                    type="text"
                    required
                    value={form.companyDb}
                    onChange={(e) => set("companyDb", e.target.value)}
                    placeholder="SBODemoUS"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    The database name shown on the SAP Business One login screen (e.g., SBODemoUS).
                  </p>
                </div>
              )}
            </div>

            {isB1 ? (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    required
                    value={form.b1Username}
                    onChange={(e) => set("b1Username", e.target.value)}
                    placeholder="svc_velaro"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Create a dedicated service account — do not use your personal SAP user.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password{" "}
                    {existingId && (
                      <span className="text-gray-400 font-normal">(leave blank to keep existing)</span>
                    )}
                  </label>
                  <input
                    type="password"
                    required={!existingId}
                    value={form.b1Password}
                    onChange={(e) => set("b1Password", e.target.value)}
                    placeholder={existingId ? "••••••••" : "SAP service account password"}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Token URL</label>
                  <input
                    type="url"
                    required
                    value={form.tokenUrl}
                    onChange={(e) => set("tokenUrl", e.target.value)}
                    placeholder="https://<subaccount>.authentication.<region>.hana.ondemand.com/oauth/token"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    The SAP BTP XSUAA token endpoint. Format: https://&lt;subaccount&gt;.authentication.&lt;region&gt;.hana.ondemand.com/oauth/token
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
                  <input
                    type="text"
                    required
                    value={form.clientId}
                    onChange={(e) => set("clientId", e.target.value)}
                    placeholder="sb-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx!bxxxxxx"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    From your SAP BTP service binding or OAuth client configuration.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client Secret{" "}
                    {existingId && (
                      <span className="text-gray-400 font-normal">(leave blank to keep existing)</span>
                    )}
                  </label>
                  <input
                    type="password"
                    required={!existingId}
                    value={form.clientSecret}
                    onChange={(e) => set("clientSecret", e.target.value)}
                    placeholder={existingId ? "••••••••" : "Client secret from BTP service binding"}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}

            {/* Allow self-signed cert */}
            <div className="flex items-start gap-3 pt-1">
              <input
                type="checkbox"
                id="sap-self-signed"
                checked={form.allowSelfSignedCert}
                onChange={(e) => set("allowSelfSignedCert", e.target.checked)}
                className="mt-0.5 rounded border-gray-300 text-blue-600"
              />
              <div>
                <label htmlFor="sap-self-signed" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Allow self-signed certificate
                </label>
                <p className="text-xs text-gray-400 mt-0.5">
                  Enable if your SAP server uses a self-signed or internal CA certificate. Not recommended for production environments.
                </p>
              </div>
            </div>

            {/* Enable toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sap-enabled"
                checked={form.enabled}
                onChange={(e) => set("enabled", e.target.checked)}
                className="rounded border-gray-300 text-blue-600"
              />
              <label htmlFor="sap-enabled" className="text-sm text-gray-700">Enable this connection</label>
            </div>

            <div className="flex justify-between pt-1">
              <button
                type="button"
                onClick={() => navigate("/integrations/sap")}
                className="text-gray-600 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Test + Save ── */}
        {step === 3 && (
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Test your connection</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Verify Velaro can reach your SAP Service Layer before saving.
                </p>
              </div>
              <button type="button" onClick={() => setStep(2)} className="text-xs text-blue-600 hover:underline">
                ← Edit credentials
              </button>
            </div>

            {/* Test button + result */}
            <div className="rounded-lg border border-slate-200 p-4 space-y-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleTest}
                  disabled={testStatus === "testing"}
                  className="flex items-center gap-2 bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700 text-sm font-medium disabled:opacity-50 transition-colors"
                >
                  {testStatus === "testing" ? (
                    <>
                      <LoadingSpinner />
                      Testing…
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Test Connection
                    </>
                  )}
                </button>

                {testStatus === "success" && (
                  <div className="flex items-center gap-2 text-green-700 text-sm">
                    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {testMessage}
                  </div>
                )}

                {testStatus === "error" && (
                  <div className="flex items-start gap-2 text-red-700 text-sm">
                    <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {testMessage}
                  </div>
                )}
              </div>

              {testStatus === "idle" && (
                <p className="text-xs text-slate-400">
                  Click "Test Connection" to verify connectivity before saving.
                </p>
              )}
            </div>

            {/* Skip option (shown after a failed test, or at any time) */}
            {testStatus !== "success" && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sap-skip-test"
                  checked={skipTest}
                  onChange={(e) => setSkipTest(e.target.checked)}
                  className="rounded border-gray-300 text-gray-500"
                />
                <label htmlFor="sap-skip-test" className="text-sm text-gray-500 cursor-pointer">
                  Skip test and save anyway
                </label>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{error}</div>
            )}

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => navigate("/integrations/sap")}
                className="text-gray-600 px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={saving || !canSave}
                title={!canSave ? "Test the connection above, or check \"Skip test and save anyway\"" : undefined}
                className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? "Saving…" : existingId ? "Save Changes" : "Connect SAP"}
              </button>
            </div>

            {!canSave && (
              <p className="text-xs text-slate-400 text-right -mt-3">
                Run a successful test first, or check "Skip test and save anyway".
              </p>
            )}
          </div>
        )}

      </form>
    </div>
  );
}
